import { ComponentType, FC, ReactNode, Ref, SVGAttributes } from 'react';
import clsx from 'clsx';

import styles from './Pill.module.css';

export type PillVariant = 'neutral' | 'gray' | 'info' | 'warning' | 'error' | 'success' | 'discovery';
export type PillSize = 'small' | 'medium';

const variantStyles: Record<PillVariant, string> = {
  neutral: styles.neutralStyle,
  gray: styles.grayStyle,
  info: styles.infoStyle,
  warning: styles.warningStyle,
  error: styles.errorStyle,
  success: styles.successStyle,
  discovery: styles.discoveryStyle,
};

const sizeStyles: Record<PillSize, string> = {
  small: styles.smallStyle,
  medium: styles.mediumStyle,
};

const iconSizes: Record<PillSize, number> = {
  small: 16,
  medium: 20,
};

type PillProps = {
  children: ReactNode;
  variant?: PillVariant;
  size?: PillSize;
  icon?: ComponentType<SVGAttributes<SVGSVGElement>>;
  iconFill?: string;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  testid?: string;
  ariaLabel?: string;
  ref?: Ref<HTMLButtonElement & HTMLSpanElement>;
};

const Pill: FC<PillProps> = ({
  children,
  variant = 'neutral',
  size = 'small',
  icon: PillIcon,
  iconFill,
  onClick,
  selected = false,
  disabled = false,
  className,
  testid,
  ariaLabel,
  ref,
}) => {
  const isInteractive = !!onClick;

  const pillClassName = clsx(
    styles.pillStyle,
    variantStyles[variant],
    sizeStyles[size],
    {
      [styles.interactiveStyle]: isInteractive,
      [styles.selectedStyle]: selected,
      [styles.disabledStyle]: disabled,
    },
    className,
  );

  const content = (
    <>
      {PillIcon && (
        <PillIcon
          className={styles.iconStyle}
          width={iconSizes[size]}
          height={iconSizes[size]}
          fill={iconFill}
          aria-hidden='true'
        />
      )}
      {children}
    </>
  );

  if (isInteractive) {
    return (
      <button
        ref={ref}
        type='button'
        className={pillClassName}
        onClick={onClick}
        disabled={disabled}
        aria-pressed={selected}
        aria-label={ariaLabel}
        data-testid={testid}
      >
        {content}
      </button>
    );
  }

  return (
    <span ref={ref} className={pillClassName} aria-label={ariaLabel} data-testid={testid}>
      {content}
    </span>
  );
};

export default Pill;
